import { sortBy } from 'lodash-es'
import type { SearchOption } from '@jana-technology/platform-ui'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'

import { IContinuousRoute } from '$/types'
import RangeSlider from 'common/components/RangeSlider'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'

import { ILocalFilter } from '..'
import { IFilterDetailsProps } from './types'

interface INetworkDropdownProps
  extends Omit<
    IFilterDetailsProps,
    'setIsLocalFilterStateChanged' | 'setCanFilterBeApplied'
  > {
  addRoute: () => void
  removeRoute: (routeId: number) => void
  selectRoute: (selections: SearchOption[], index: number) => void
  updateRouteRange: (routeId: number, range: number[]) => void
  routeOptions: SearchOption[]
  selectedClassLocations: SearchOption[]
  classLocationOptions: SearchOption[]
  riskLevelOptions: SearchOption[]
  consequenceOptions: SearchOption[]
  localFilter: ILocalFilter
}

interface IRouteSelectRowProps
  extends Pick<
    INetworkDropdownProps,
    'routeOptions' | 'selectRoute' | 'updateRouteRange' | 'removeRoute'
  > {
  routeIndex: number
  selectedRoute: IContinuousRoute
  testIndex: number
  segment?: IContinuousRoute
}

const RouteSelectRow = ({
  segment,
  routeOptions,
  selectRoute,
  selectedRoute,
  updateRouteRange,
  removeRoute,
  routeIndex,
  testIndex
}: IRouteSelectRowProps) => (
  <div className='flex gap-x-4 py-2' data-testid={`route ${testIndex}`}>
    <div className='w-64'>
      <SelectDropdownWrapper
        matchTriggerWidth
        className='w-full'
        options={sortBy(routeOptions, [
          (i) => i.label.toLowerCase(),
          (i) => i.label
        ])}
        onChange={(_, selection) => {
          if (!Array.isArray(selection)) {
            selectRoute([selection], routeIndex)
          }
        }}
        value={String(selectedRoute.id)}
        disableClear={true}
      />
    </div>
    <div className='flex w-96 flex-1 flex-col'>
      <div className='flex gap-x-2'>
        <div className='flex-1'>
          <RangeSlider
            disabled={!selectedRoute.id || selectedRoute.id < 0}
            enableInput
            min={Math.min(
              segment?.fromMeasure ?? 0,
              selectedRoute.initialFromMeasure
            )}
            max={selectedRoute.initialToMeasure}
            value={[selectedRoute.fromMeasure, selectedRoute.toMeasure]}
            setRange={(range) => updateRouteRange(selectedRoute.rowId, range)}
          />
        </div>
        <button
          className='text-gray-700'
          onClick={() => removeRoute(selectedRoute.rowId)}
          data-testid='clear-first-segment'
        >
          <CancelIcon fontSize='small' />
        </button>
      </div>
    </div>
  </div>
)

export const NetworkDropdown = ({
  localFilter,
  routeOptions,
  selectRoute,
  updateRouteRange,
  addRoute,
  removeRoute,
  onHMCASelection,
  onClassLocationSelection,
  onRiskLevelSelection,
  selectedRoutes,
  selectedHmca,
  consequenceOptions,
  riskLevelOptions,
  selectedRiskLevels,
  classLocationOptions,
  selectedClassLocations
}: INetworkDropdownProps) => {
  // segement is needed for a static min value, if it's negative
  return (
    <div>
      <div className='m-6'>
        <div className='font-semibold'>Routes</div>
        <div className='mt-6 flex gap-x-4 font-medium'>
          <div className='w-64'>Route</div>
          <div>Range</div>
        </div>
        {selectedRoutes.map((route, index) => (
          <RouteSelectRow
            segment={localFilter.segments.find(
              (segment) => segment.rowId === route.rowId
            )}
            key={route.rowId}
            routeIndex={route.rowId}
            selectedRoute={route}
            routeOptions={routeOptions}
            selectRoute={selectRoute}
            updateRouteRange={updateRouteRange}
            removeRoute={removeRoute}
            testIndex={index}
          />
        ))}
        <button
          className='mt-4 flex items-center leading-none text-lighthouseBlue'
          onClick={addRoute}
        >
          <AddIcon />
          <span>Add Range</span>
        </button>
      </div>
      <hr className='h-2 bg-gray-200' />
      <div className='m-6 mb-12'>
        <div className='font-semibold'>Filter Within Selected Ranges</div>
        <div className='flex items-center justify-between border-b py-3'>
          <span className='font-medium'>Consequence Area</span>
          <SelectDropdownWrapper
            className='w-96'
            mode='tags'
            options={consequenceOptions}
            value={selectedHmca.map(({ value }) => value)}
            onChange={(_, selections) =>
              onHMCASelection(selections as SearchOption[])
            }
            placeholder='All'
          />
        </div>
        <div className='flex items-center justify-between border-b py-3'>
          <span className='font-medium'>Class Location</span>
          <SelectDropdownWrapper
            className='w-96'
            mode='tags'
            options={classLocationOptions}
            value={selectedClassLocations.map(({ value }) => value)}
            onChange={(_, selections) =>
              onClassLocationSelection(selections as SearchOption[])
            }
            placeholder='All'
          />
        </div>
        <div className='flex items-center justify-between py-3'>
          <span className='font-medium'>Risk Level</span>
          <SelectDropdownWrapper
            className='w-96'
            mode='tags'
            options={riskLevelOptions}
            value={selectedRiskLevels.map(({ value }) => value)}
            onChange={(_, selections) =>
              onRiskLevelSelection(selections as SearchOption[])
            }
            placeholder='All'
          />
        </div>
      </div>
    </div>
  )
}
